import { useCallback } from 'react';

import { message } from 'antd';
import { useTranslation } from 'react-i18next';

import { useBackgroundJob } from '@shared/BackgroundJobManager';

// task 的 resolved 結果（直接是 JSON）
interface BatchTaskResult {
  success?: boolean;
  data?: {
    job_id?: string;
    [key: string]: unknown;
  };
  message?: string;
  [key: string]: unknown;
}

type BatchTask =
  | Promise<unknown>
  | (() => Promise<unknown>)
  | null
  | undefined;

interface BatchOperationPayload {
  task: BatchTask;
  // jobName 在某些 caller 透過條件分支設定，可能為 null；hook 內僅作為 addJob 第二參數透傳
  jobName: string | null;
  onSuccess?: ((data: unknown) => void) | null;
  onError?: ((error: unknown) => void) | null;
  showSuccessMessage?: boolean;
  successMessage?: string | null;
  // 保留 callers 仍會傳入但 hook 內未使用的欄位（如 url / selected / params），
  // 避免影響原本 runtime 行為。
  [key: string]: unknown;
}

// addJob 由 callers 從 BackgroundJobManager / OperatorNotificationContext 等多個 .js context 取得，
// 各 context 對 onComplete 簽名定義略有差異；hook 內只負責呼叫，故放寬到 ...args: any[]
type AddJobFn = (jobId: string, jobName: string, ...args: any[]) => void;

export function useBatchOperation(
  service: unknown,
  addJob?: AddJobFn
): (payload: BatchOperationPayload) => Promise<BatchTaskResult | undefined> {
  const { t } = useTranslation();
  const { registerJob } = useBackgroundJob() as { registerJob?: (jobId: string) => void };

  const executeBatchOperation = useCallback(async ({
    task,
    jobName,
    onSuccess = null,
    onError = null,
    showSuccessMessage = false,
    successMessage = null
  }: BatchOperationPayload) => {
    try {
      // task is a Promise or a function returning one
      // It is expected to resolve to the JSON result directly
      const raw = typeof task === 'function' ? await task() : await task;
      const result = (raw ?? {}) as BatchTaskResult;

      // 背景任務處理
      if (result.success && result.data?.job_id) {
        const jobId = result.data.job_id;
        console.log(`[useBatchOperation] 🚀 Job started: ${jobId} (${jobName})`);

        // 註冊 Job 所有權，確保此 Tab 會處理相關通知
        if (registerJob) {
          registerJob(jobId);
        }

        // 顯示任務開始訊息
        message.info(t('background_job.messages.start') as string);

        if (addJob && typeof addJob === 'function') {
          console.log(`[useBatchOperation] 📝 Registering Job ${jobId} with Manager...`);
          addJob(
            jobId,
            jobName ?? '',
            (data: unknown) => {
              console.log(`[useBatchOperation] 🏁 Job ${jobId} completion callback triggered!`);
              // Job 完成時的回調
              if (onSuccess && typeof onSuccess === 'function') {
                console.log(`[useBatchOperation] 🏃 Executing onSuccess callback for ${jobId}`);
                onSuccess(data);
              }
              if (showSuccessMessage && successMessage) {
                message.success(successMessage);
              }
            }
          );
        } else {
          console.warn('[useBatchOperation] ⚠️ addJob function not found, progress tracking might fail.');
        }
      } else {
        // 非背景任務，直接執行回調
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(result.data);
        }
        if (showSuccessMessage && successMessage) {
          message.success(successMessage);
        }
      }

      return result;
    } catch (error) {
      console.error('[useBatchOperation] Error:', error);

      // 自定義錯誤處理
      if (onError && typeof onError === 'function') {
        onError(error);
      } else {
        // 預設錯誤提示
        message.error(t('common.error.operation_failed') as string);
      }

      throw error;
    }
  }, [addJob, t]);

  return executeBatchOperation;
}

export default useBatchOperation;
